import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading";
import { requiredValidation } from "@via/utils/form-validators";
import { useEffect, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useUpdateCustomInstruction } from "@via/pages/account/profile/integration-tokens/custom-instructions/custom-instructions.api";
import FormInputComponent from "@via/components/inputs/form-input-component";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component";
import {
  UpdateAIInstructionRequestBody,
  AIInstruction,
  UpdateAIInstructionQueryParams,
} from "common";

export default function EditCustomInstructionForm({
  setOpen,
  customInstruction,
}: {
  setOpen: (open: boolean) => void;
  customInstruction: AIInstruction | null;
}): JSX.Element {
  const handleClose = () => setOpen(false);

  const { control, handleSubmit } = useForm<UpdateAIInstructionRequestBody>({
    mode: "onChange",
    defaultValues: {
      name: customInstruction!.name,
      instruction: customInstruction!.instruction,
    },
  });

  const {
    mutateAsync: updateCustomInstruction,
    isPending: isUpdateCustomInstructionLoading,
    isSuccess: isUpdateCustomInstructionSuccess,
  } = useUpdateCustomInstruction();

  const handleUpdateCustomInstruction = async (
    payload: UpdateAIInstructionRequestBody
  ) => {
    const { name, instruction } = payload;
    const submission: UpdateAIInstructionRequestBody &
      UpdateAIInstructionQueryParams = {
      id: customInstruction!.id,
      name,
      instruction,
    };
    await updateCustomInstruction(submission);
  };

  useEffect(() => {
    isUpdateCustomInstructionSuccess && handleClose();
  }, [isUpdateCustomInstructionSuccess]);

  const onSubmit = handleSubmit(handleUpdateCustomInstruction);

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              Custom Instruction
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Update the custom instruction details.
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <FormInputComponent
                control={control}
                name="name"
                label="Instruction Name"
                placeholder="Enter Instruction Name"
                validations={requiredValidation("Instruction Name")}
                showRequiredField
              />
              <FormTextAreaComponent
                control={control}
                name="instruction"
                label="Instruction"
                placeholder="Enter Your Instruction"
                validations={requiredValidation("Instruction")}
                showRequiredField
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="edit-custom-instruction-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="edit-custom-instruction-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isUpdateCustomInstructionLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
}
